import { useState, type CSSProperties } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Clock, RotateCcw } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPublicContributionStatus,
  type PublicContributionStatus,
  type PublicEvent,
} from "@/services/api/publicEvents";

interface ContributionStatusCheckProps {
  event: PublicEvent;
  /** Lets each template style the trigger to match its own look; falls back to a plain text link. */
  triggerClassName?: string;
  /** Inline style (e.g. template palette color) applied alongside triggerClassName. */
  triggerStyle?: CSSProperties;
}

/**
 * JUB-98: guest-visible entry point for the JUB-79 contribution status
 * endpoint. A guest who closed the confirmation dialog (or whose payment is
 * still settling) has no other way to re-check whether their money arrived —
 * this renders a small "Verifică o contribuție" affordance that opens a
 * dialog for entering the reference code they were shown/copied at payment
 * time.
 *
 * Anti-enumeration: this component never displays anything the backend
 * doesn't already guard — the reference code is an unguessable UUID, the
 * endpoint is IP rate-limited (30/min), and "code doesn't exist" vs "code
 * belongs to another event" return the identical 404 envelope. The response
 * itself carries no other guest's name/email, only status + amount + gift.
 */
export function ContributionStatusCheck({ event, triggerClassName, triggerStyle }: ContributionStatusCheckProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PublicContributionStatus | null>(null);

  const eventIdentifier = (event.url || event.uuid).replace(/^@/, "");

  const reset = () => {
    setReference("");
    setLoading(false);
    setError(null);
    setResult(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) reset();
  };

  const handleCheck = async () => {
    const trimmed = reference.trim();
    if (!trimmed) {
      setError(t("gifts.statusCheck.referenceRequired"));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const status = await getPublicContributionStatus(eventIdentifier, trimmed);
      setResult(status);
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 429) {
        setError(t("gifts.statusCheck.rateLimited"));
      } else {
        // 404 ("no such code" or "belongs to another event") and any other
        // failure resolve to the same generic message — never hint at which.
        setError(t("gifts.statusCheck.notFound"));
      }
    } finally {
      setLoading(false);
    }
  };

  const statusMeta: Record<
    PublicContributionStatus["status"],
    { icon: typeof CheckCircle2; className: string; labelKey: string }
  > = {
    paid: { icon: CheckCircle2, className: "text-green-600", labelKey: "gifts.statusCheck.statusPaid" },
    pending: { icon: Clock, className: "text-amber-600", labelKey: "gifts.statusCheck.statusPending" },
    refunded: { icon: RotateCcw, className: "text-muted-foreground", labelKey: "gifts.statusCheck.statusRefunded" },
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          triggerClassName ??
          "text-sm underline underline-offset-4 opacity-70 hover:opacity-100 transition-opacity"
        }
        style={triggerStyle}
        data-testid="contribution-status-check-trigger"
      >
        {t("gifts.statusCheck.trigger")}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-[90vw] max-w-md" data-testid="contribution-status-check-dialog">
          <DialogHeader>
            <DialogTitle>{t("gifts.statusCheck.title")}</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">{t("gifts.statusCheck.description")}</p>

          <div className="space-y-2">
            <Label htmlFor="contribution-status-reference">{t("gifts.statusCheck.referenceLabel")}</Label>
            <Input
              id="contribution-status-reference"
              value={reference}
              onChange={(e) => {
                setReference(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCheck();
                }
              }}
              placeholder={t("gifts.statusCheck.referencePlaceholder")}
              autoComplete="off"
              data-testid="contribution-status-check-input"
            />
          </div>

          {error && (
            <Alert variant="destructive" data-testid="contribution-status-check-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <div
              className="space-y-3 p-4 bg-muted/30 rounded-lg text-sm"
              data-testid="contribution-status-check-result"
            >
              <div className={`flex items-center gap-2 font-semibold ${statusMeta[result.status].className}`}>
                {(() => {
                  const Icon = statusMeta[result.status].icon;
                  return <Icon className="h-4 w-4" aria-hidden="true" />;
                })()}
                {t(statusMeta[result.status].labelKey)}
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t("gifts.confirmation.amountLabel")}</span>
                <span className="font-semibold" data-testid="contribution-status-check-amount">
                  {result.amount.toFixed(2)} {result.currency}
                </span>
              </div>
              {result.giftName && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{t("gifts.confirmation.giftLabel")}</span>
                  <span className="font-medium text-right">{result.giftName}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCheck}
              disabled={loading || !reference.trim()}
              data-testid="contribution-status-check-submit"
            >
              {loading ? t("common.loading") : t("gifts.statusCheck.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
